/**
 * The design's two number shapes (design-system.md §1): integers with thousands
 * separators, and percentages as a whole number plus `%`.
 *
 * Both fall back to an em dash rather than rendering `NaN`. Every caller is a
 * KPI tile or a ring counter reading an optional API field — `entryCount` and
 * `prizeDeliveryProgressPercent` are both optional on `ContestSummary` — and a
 * tile reading "NaN" is worse than one reading "—", which the design already
 * uses for "we don't know".
 */
import { ADMIN_LOCALE } from './time';

const integerFormatter = new Intl.NumberFormat(ADMIN_LOCALE, { maximumFractionDigits: 0 });

export const formatInteger = (value: number | null | undefined, fallback = '—'): string =>
  typeof value === 'number' && Number.isFinite(value) ? integerFormatter.format(value) : fallback;

/**
 * Takes a percentage, not a fraction: `42` renders `42%`.
 *
 * That matches the API, whose `prizeDeliveryProgressPercent` is already scaled,
 * so no caller has to remember which side of the hundred it is on.
 *
 * Rounds down. A delivery run at 99.6% has not finished, and a tile reading
 * `100%` beside a queue that is still draining is the kind of number the design
 * calls "stats slop".
 */
export const formatPercent = (value: number | null | undefined, fallback = '—'): string =>
  typeof value === 'number' && Number.isFinite(value) ? `${Math.floor(value)}%` : fallback;
